import { IBayLevelFinding, IValidationInput, IValidationOutput, IValidator } from "../types";

import { OVD_PLAUSIBILITY } from "../structure/consts";
import { createValidationOutput } from "../helpers/createValidationOutput";
import { isPlainObject } from "../structure/patterns";

const name = "rowWeightsValidator";
const okTranslationKey = "validators:bays.rowWeights.ok";
const errorTranslationKey = "validators:bays.rowWeights.error";

export type TRowWeightField = "rowWeight" | "bottomWeight";

export interface IRowWeightFinding extends IBayLevelFinding {
  /** Negative, or above `OVD_PLAUSIBILITY.MAX_ROW_WEIGHT_G` */
  code: "implausible";
  /** `infoByContLength.40.rowWeight` or `perRowInfo.each.05.rowInfoByLength.40.rowWeight` */
  path: string;
  field: TRowWeightField;
  size: number;
  /** Set when the weight is in a row's `rowInfoByLength` */
  row?: string;
  value: number;
}

export interface IRowWeightsOutput {
  findings: IRowWeightFinding[];
}

/**
 * Row and bottom weights that no vessel has: negative, or above
 * `OVD_PLAUSIBILITY.MAX_ROW_WEIGHT_G` (1000 t). STAF sources write `32769` where a
 * weight is unknown; after the conversion to grams that is `3276900000`, which is
 * what this finds in practice.
 *
 * Such a weight is worse than missing: a `rowInfoByLength` entry for a 40'+ size that
 * holds nothing but a placeholder weight makes the 40s detectors believe the Bay holds
 * 40s, so a pair looks split. `fixRowWeights` drops them.
 */
export const rowWeightsValidator: IValidator<IRowWeightsOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: rowWeightsValidatorFn,
  severity: "warning",
  stage: "data",
};

export function rowWeightsValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IRowWeightsOutput> {
  const findings: IRowWeightFinding[] = [];
  const baysData: unknown[] = Array.isArray(json?.baysData) ? json.baysData : [];

  baysData.forEach((entry) => {
    if (!isPlainObject(entry)) return;
    const isoBay = String(entry["isoBay"]);
    const level = entry["level"] as IBayLevelFinding["level"];

    const check = (dict: unknown, pathPrefix: string, row?: string) => {
      if (!isPlainObject(dict)) return;
      Object.keys(dict).forEach((sizeKey) => {
        const e = dict[sizeKey];
        if (!isPlainObject(e)) return;
        (["rowWeight", "bottomWeight"] as const).forEach((field) => {
          const value = e[field];
          if (typeof value !== "number") return;
          if (value < 0 || value > OVD_PLAUSIBILITY.MAX_ROW_WEIGHT_G) {
            findings.push({
              code: "implausible",
              path: `${pathPrefix}.${sizeKey}.${field}`,
              isoBay,
              level,
              field,
              size: Number(sizeKey),
              row,
              value,
            });
          }
        });
      });
    };

    check(entry["infoByContLength"], "infoByContLength");

    const each = isPlainObject(entry["perRowInfo"]) ? entry["perRowInfo"]["each"] : undefined;
    if (isPlainObject(each)) {
      Object.keys(each).forEach((row) => {
        const r = each[row];
        if (isPlainObject(r))
          check(r["rowInfoByLength"], `perRowInfo.each.${row}.rowInfoByLength`, row);
      });
    }
  });

  return createValidationOutput(rowWeightsValidator, findings, { findings });
}
